// Shared computation of positive-ROI eligibility recommendations.
// Both `EligibilityAdvisor.js` (UI) and `pdfGenerator.js` (PDF) need the same
// rec data to stay in sync. This util returns plain data (no JSX) so the
// PDF generator can render it without dragging in React.
//
// Each returned recommendation matches the same `positiveRoiCount` semantics
// used in the on-screen advisor:
//   - waiting-period:        employees who become eligible by reducing wait
//   - hours-threshold:       employees who become eligible by lowering hours min
//   - near-threshold-salary: employees whose raise pays for itself via FICA
//
// 1099-reclass / union / seasonal recommendations are intentionally excluded
// because their `positiveRoiCount` is 0 — they involve legal/CBA/policy
// implications and the FICA recovery typically does not exceed the
// reclassification/contractual cost. The recommendation engine "works for
// the employer's favor" — only show actions that pay off.
//
// Public:
//   computePositiveRecommendations(analyzed, config) -> Recommendation[]
//
// Recommendation shape:
//   {
//     id, title, impact, positiveRoiCount,
//     totalAnnualFica,         // total FICA $ unlocked across the rec
//     headline,                // single-line summary suitable for a PDF row
//     scenarios: [{ label, count, fica }], // optional detail bullets
//     rows: [{ name, salary, gap, monthlyGap, ficaSavings, netImpact }]?,
//   }

import { optimizeEmployeeWithEligibility } from './calculations';

const DEFAULT_WAITING_SCENARIOS = [0, 30, 60, 90];

function fmt(n) {
  return Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

export function computePositiveRecommendations(analyzed, config) {
  const recs = [];

  // ---- 1) Waiting Period Reduction ----
  const waitingEmployees = (analyzed || []).filter((e) => e.eligibility?.status === 'waiting');
  if (waitingEmployees.length > 0) {
    const currentWaiting = config.employerPlanRules?.waiting_period_days ?? 90;
    const scenarios = (config.advisorSettings?.waitingPeriodScenarios || DEFAULT_WAITING_SCENARIOS)
      .filter((d) => d < currentWaiting);

    const scenarioImpacts = scenarios.map((days) => {
      let count = 0;
      let fica = 0;
      waitingEmployees.forEach((e) => {
        const daysWorked = e.hireDate
          ? Math.floor((Date.now() - new Date(e.hireDate).getTime()) / (1000 * 60 * 60 * 24))
          : 0;
        if (daysWorked >= days) {
          const simulated = optimizeEmployeeWithEligibility(
            { ...e, hireDate: new Date(Date.now() - (days + 1) * 86400000) },
            config
          );
          if (simulated.hasViable && simulated.optimal) {
            count += 1;
            fica += simulated.optimal.employerAnnualFica || 0;
          }
        }
      });
      return { label: `At ${days}-day wait`, days, count, fica };
    });

    const best = scenarioImpacts.reduce(
      (acc, s) => (s.count > acc.count ? s : acc),
      { count: 0, fica: 0 }
    );
    if (best.count > 0) {
      recs.push({
        id: 'waiting-period',
        title: 'Reduce Waiting Period',
        impact: 'high',
        positiveRoiCount: best.count,
        totalAnnualFica: best.fica,
        headline: `${waitingEmployees.length} employee${waitingEmployees.length === 1 ? ' is' : 's are'} in waiting period — reducing the wait converts up to ${best.count} sooner.`,
        scenarios: scenarioImpacts.filter((s) => s.count > 0),
        rationale: 'No employer cost — eligibility flips by calendar alone. Best-case unlock applied.',
      });
    }
  }

  // ---- 2) Lower Minimum Hours Requirement ----
  const belowHoursEmployees = (analyzed || []).filter((e) => e.eligibility?.status === 'hours');
  if (belowHoursEmployees.length > 0 && config.employerPlanRules?.minimum_weekly_hours) {
    const currentThreshold = config.employerPlanRules.minimum_weekly_hours;
    const candidates = (config.advisorSettings?.hoursThresholdScenarios || [30, 25, 20, 15])
      .filter((t) => t < currentThreshold);

    const impactByThreshold = candidates.map((threshold) => {
      let count = 0;
      let fica = 0;
      belowHoursEmployees.forEach((e) => {
        if ((e.weeklyHours || 0) >= threshold) {
          const simulated = optimizeEmployeeWithEligibility(e, {
            ...config,
            employerPlanRules: {
              ...config.employerPlanRules,
              minimum_weekly_hours: threshold,
            },
          });
          if (simulated.hasViable && simulated.optimal) {
            count += 1;
            fica += simulated.optimal.employerAnnualFica || 0;
          }
        }
      });
      return { label: `At ${threshold} hrs/week min`, threshold, count, fica };
    });

    const best = impactByThreshold.reduce(
      (acc, s) => (s.count > acc.count ? s : acc),
      { count: 0, fica: 0 }
    );
    if (best.count > 0) {
      recs.push({
        id: 'hours-threshold',
        title: 'Adjust Minimum Hours Requirement',
        impact: best.count >= 5 ? 'high' : 'medium',
        positiveRoiCount: best.count,
        totalAnnualFica: best.fica,
        headline: `${belowHoursEmployees.length} employee${belowHoursEmployees.length === 1 ? '' : 's'} sit just below the ${currentThreshold}-hr/week threshold. Lowering it qualifies up to ${best.count} more.`,
        scenarios: impactByThreshold.filter((s) => s.count > 0),
        rationale: 'Policy-only change (no direct payroll cost). Verify plan document allows the new threshold.',
      });
    }
  }

  // ---- 3) Near-Eligible Salary Raises (positive-ROI only) ----
  const nearBuffer = config.advisorSettings?.nearEligibleSalaryBuffer || 3000;
  const nearCandidates = (analyzed || []).filter(
    (e) => e.eligibility?.status === 'salary' && e.breakEvenSalary
  );
  if (nearCandidates.length > 0) {
    const nearThreshold = nearCandidates.filter(
      (e) => e.breakEvenSalary - e.salary <= nearBuffer
    );
    if (nearThreshold.length > 0) {
      const enriched = nearThreshold.map((e) => {
        const gap = e.breakEvenSalary - e.salary;
        const monthlyGap = gap / 12;
        const simulated = optimizeEmployeeWithEligibility(
          { ...e, salary: e.breakEvenSalary },
          config
        );
        const ficaSavings =
          simulated.hasViable && simulated.optimal ? simulated.optimal.employerAnnualFica : 0;
        const netImpact = ficaSavings - gap;
        return {
          name: e.name || 'Employee',
          salary: e.salary,
          gap,
          monthlyGap,
          ficaSavings,
          netImpact,
        };
      });
      const positives = enriched
        .filter((r) => r.netImpact > 0)
        .sort((a, b) => b.netImpact - a.netImpact);
      if (positives.length > 0) {
        const totalFica = positives.reduce((s, r) => s + r.ficaSavings, 0);
        const totalNet = positives.reduce((s, r) => s + r.netImpact, 0);
        recs.push({
          id: 'near-threshold-salary',
          title: 'Near-Eligible Employees — Raise Pays for Itself',
          impact: 'medium',
          positiveRoiCount: positives.length,
          totalAnnualFica: totalFica,
          totalNetImpact: totalNet,
          headline: `${positives.length} employee${positives.length === 1 ? '' : 's'} would become eligible after a small raise that pays for itself through FICA savings (${fmt(totalNet)}/yr net to employer).`,
          rows: positives,
          rationale: 'Only employees where annual FICA recovery exceeds the raise cost are included.',
        });
      }
    }
  }

  // Filter to only positive-ROI recs (defensive — should already be true)
  return recs.filter((r) => (r.positiveRoiCount || 0) > 0);
}
